import React, { useState, useEffect } from "react";
import {
    Button,
    Typography,
    Box,
    useMediaQuery,
    IconButton,
    Divider
} from "@mui/material";
import {
    ChevronLeft,
    ChevronRight,
    X as XIcon,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getListofLostPets, getListofFoundPets } from "../services/api";
import { convertFromISO } from "../utils/dateValidation";
import { useLocation } from "react-router-dom";


export default function PetSearchResult() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isMobile = useMediaQuery("(max-width:900px)");
    const location = useLocation();
    const possibleMatches = location.state?.possibleMatches || [];
    const [searchParams] = useSearchParams();
    const petlost = searchParams.get("petlost");

    const [pets, setPets] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [filteredPets, setFilteredPets] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const petsPerPage = 10;
    const [sort, setSort] = useState({ key: null, direction: null });
    const [selectedPet, setSelectedPet] = useState(null);
    const [pageInput, setPageInput] = useState(currentPage.toString());
    const [carouselIndex, setCarouselIndex] = useState(0);

    useEffect(() => {
        window.scrollTo(0, 0);
        const lostPets = possibleMatches.map((lostPets) => lostPets);
        setPets(possibleMatches || []);
        setFilteredPets(possibleMatches || []);
        setTotalResults(possibleMatches.length);
        setTotalPages(Math.max(1, Math.ceil(possibleMatches.length / petsPerPage)));
    }, []);

    // Reset carousel index when a new pet is selected
    useEffect(() => {
        setCarouselIndex(0);
    }, [selectedPet]);

    const handleSort = (key) => {
        setSort((prev) => {
            if (prev.key !== key) return { key, direction: "asc" };
            if (prev.direction === "asc") return { key, direction: "desc" };
            return { key: null, direction: null };
        });
    };

    // Page input handlers
    const handlePageClick = () => {
        setPageInput(currentPage.toString());
    };

    const handlePageInputChange = (e) => {
        const value = e.target.value;
        // Only allow numbers
        if (/^\d*$/.test(value)) {
            setPageInput(value);
        }
    };

    const handlePageInputSubmit = () => {
        const pageNum = parseInt(pageInput);
        if (pageNum >= 1 && pageNum <= totalPages) {
            setCurrentPage(pageNum);
        } else {
            setPageInput(currentPage.toString());
        }
    };

    const handlePageInputKeyPress = (e) => {
        if (e.key === "Enter") {
            handlePageInputSubmit();
        } else if (e.key === "Escape") {
            setPageInput(currentPage.toString());
        }
    };

    // Update pageInput when currentPage changes
    useEffect(() => {
        setPageInput(currentPage.toString());
    }, [currentPage]);

    const handleSearchChange = (event) => {
        setSearchText(event.target.value);
    };

    const clearSearch = () => {
        setSearchText("");
        setStatusFilter("all");
    };

    // Sorting
    let sortedPets = [...filteredPets].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'zh-Hans-CN', { sensitivity: 'base' })
    );

    // Pagination
    const indexOfLastPet = currentPage * petsPerPage;
    const indexOfFirstPet = indexOfLastPet - petsPerPage;
    const currentPets = sortedPets.slice(indexOfFirstPet, indexOfLastPet);

    function daysSinceLost(lostDate) {
        if (!lostDate) return "";
        const today = new Date();
        const lost = new Date(lostDate);
        today.setHours(0, 0, 0, 0);
        lost.setHours(0, 0, 0, 0);
        const diffTime = today - lost;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    }

    return (
        <Box
            sx={{ background: "#FCF8F3", minHeight: "100vh", pt: 15, position: "relative" }}
        >
            <Navbar />
            <Box sx={{ color: "black", minWidth: 0, position: "absolute", left: isMobile ? 10 : 80, display: 'flex', }}>
                <Button
                    sx={{ color: "black", minWidth: 0, cursor: 'pointer' }}
                    onClick={() => navigate("/pet_found_search")}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="20" viewBox="0 0 14 24" fill="none">
                        <path d="M12 22L2 12L12 2" stroke="#050505" stroke-width="3.33333" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </Button>
                <Typography sx={{ ml: 1, fontSize: isMobile ? 26 : 34, fontWeight: 700 }}>{t('SearchResults')}</Typography>
            </Box>

            <Box sx={{ display: 'flex', pt: 5, px: isMobile ? 2 : 10, gap: 5, alignItems: 'flex-start', mt: 5, position: 'relative' }}>
                <Box sx={{ display: 'flex', flexDirection: "column", gap: 2, alignItems: 'flex-start', }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Typography
                            variant="h6"
                            fontWeight={700}
                        >
                            {t('AfterInt')}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: !isMobile && 5, flexWrap: 'wrap', justifyContent: isMobile && "space-between" }}>
                        {currentPets.map((pet, idx) => (
                            <Box
                                key={pet.lostPet._id || idx}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    justifyContent: "flex-start",
                                    borderRadius: 2,
                                    background: "#FFFFFF",
                                    mb: 2,
                                    position: 'relative',
                                    height: 400,
                                    width: isMobile ? '170px' : '210px',
                                    cursor: 'pointer',
                                    "&:hover": { boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)" },
                                }}
                                onClick={() => setSelectedPet(pet)}
                            >

                                <div style={{
                                    width: isMobile ? 170 : 210,
                                    height: 210,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    background: '#f0f0f0'
                                }}>
                                    <img
                                        src={pet.lostPet.breedimage && pet.lostPet.breedimage[0]}
                                        alt=""
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                            borderRadius: "2px 2px 0 0",
                                        }}
                                    />
                                </div>
                                <div style={{ position: 'absolute', top: 5, left: 5, padding: "5px 10px", backgroundColor: pet.lostPet.status === "遺失" ? '#FFDBDB' : '#D4F4DD', borderRadius: 20, color: pet.lostPet.status === "遺失" ? '#E63231' : '#29bd49ff' }}>{pet.lostPet.status === "遺失" ? t('Lost') : t('Found')}</div>
                                <Box sx={{ display: 'flex', flexDirection: 'column', p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography fontSize={14} fontWeight={700}>{pet.lostPet.name}</Typography>
                                        <Typography fontSize={14} sx={{ color: '#666666' }}>
                                            {pet.lostPet.lostDate ? `${daysSinceLost(pet.lostPet.lostDate)} days ago` : `${daysSinceLost(pet.lostPet.foundDate)} days ago`}
                                        </Typography>
                                    </Box>
                                    <Typography fontSize={14} fontWeight={700} sx={{ color: '#666666' }}>{pet.lostPet.breed}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 20 }}>
                                                {/* Location SVG */}
                                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" strokeOpacity="0.6" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M13.3385 6.66927C13.3385 9.99794 9.64588 13.4646 8.40588 14.5353C8.29036 14.6221 8.14974 14.6691 8.00521 14.6691C7.86068 14.6691 7.72006 14.6221 7.60454 14.5353C6.36454 13.4646 2.67188 9.99794 2.67188 6.66927C2.67188 5.25478 3.23378 3.89823 4.23397 2.89803C5.23417 1.89784 6.59072 1.33594 8.00521 1.33594C9.4197 1.33594 10.7763 1.89784 11.7764 2.89803C12.7766 3.89823 13.3385 5.25478 13.3385 6.66927Z" stroke="black" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M8 8.66406C9.10457 8.66406 10 7.76863 10 6.66406C10 5.55949 9.10457 4.66406 8 4.66406C6.89543 4.66406 6 5.55949 6 6.66406C6 7.76863 6.89543 8.66406 8 8.66406Z" stroke="black" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </Box>
                                        </Box>
                                        <Box
                                            fontSize={14}
                                            sx={{
                                                color: '#666666',
                                                width: "150px",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis"
                                            }}
                                        >
                                            {pet.lostPet.lostLocation ? pet.lostPet.lostLocation : pet.lostPet.foundLocation}
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 20 }}>
                                                {/* Calendar SVG */}
                                                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" strokeOpacity="0.8" xmlns="http://www.w3.org/2000/svg">
                                                    <g clipPath="url(#clip0_7005_34948)">
                                                        <path d="M4.26562 1.0625V3.19583" stroke="#666666" strokeWidth="1.06667" strokeLinecap="round" strokeLinejoin="round" />
                                                        <path d="M8.53125 1.0625V3.19583" stroke="#666666" strokeWidth="1.06667" strokeLinecap="round" strokeLinejoin="round" />
                                                        <path d="M10.1349 2.13281H2.66823C2.07913 2.13281 1.60156 2.61038 1.60156 3.19948V10.6661C1.60156 11.2552 2.07913 11.7328 2.66823 11.7328H10.1349C10.724 11.7328 11.2016 11.2552 11.2016 10.6661V3.19948C11.2016 2.61038 10.724 2.13281 10.1349 2.13281Z" stroke="#666666" strokeWidth="1.06667" strokeLinecap="round" strokeLinejoin="round" />
                                                        <path d="M1.60156 5.33594H11.2016" stroke="#666666" strokeWidth="1.06667" strokeLinecap="round" strokeLinejoin="round" />
                                                    </g>
                                                    <defs>
                                                        <clipPath id="clip0_7005_34948">
                                                            <rect width="12.8" height="12.8" fill="white" />
                                                        </clipPath>
                                                    </defs>
                                                </svg>
                                            </Box>
                                        </Box>
                                        <Box fontSize={14} sx={{ color: '#666666' }}>{pet.lostPet.lostDate ? convertFromISO(pet.lostPet.lostDate) : convertFromISO(pet.lostPet.foundDate)}</Box>
                                    </Box>
                                    <Typography fontSize={14} sx={{ color: '#666666', width: "100%" }}>{pet.lostPet.description}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        my: 2,
                    }}>
                        <Typography sx={{ fontSize: isMobile ? 12 : 16 }}>
                            {t("NoMatchingResults")}{' '}
                            <span onClick={() => navigate("/all_lost_pets")} style={{ textDecoration: 'underline', cursor: 'pointer', color: '#65A8FB' }}>
                                {t("ViewallLostPets")}
                            </span>
                            {' '}{t("or")}{' '}
                            <span onClick={() => petlost ? navigate("/pet_lost_member") : navigate("/pet_found_report")} style={{ textDecoration: 'underline', cursor: 'pointer', color: '#65A8FB' }}>
                                {petlost ? t("ContinueFillLost") : t("ContinueFillFound")}
                            </span>
                        </Typography>
                    </Box>
                </Box>

            </Box>
            {selectedPet && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        bgcolor: 'rgba(0,0,0,0.4)',
                        zIndex: 2000,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                    onClick={() => setSelectedPet(null)}
                >
                    <Box
                        sx={{
                            background: "#fff",
                            borderRadius: 4,
                            p: isMobile ? 4 : 5,
                            width: isMobile ? '95vw' : 1280,
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Close button */}
                        <IconButton
                            sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}
                            onClick={() => setSelectedPet(null)}
                        >
                            <CloseIcon />
                        </IconButton>
                        {/* Left: Image */}
                        <Typography variant="h4" fontWeight={700} mb={2} fontSize={20}>
                            {t('Number')} #{selectedPet.lostPet._id}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 4, alignItems: isMobile && 'center', }}>
                            <Box sx={{ flex: 1, display: 'flex', maxWidth: 320, flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                                {/* Carousel */}
                                <Box sx={{ position: 'relative', width: 320, height: isMobile ? 220 : 320 }}>
                                    {/* Left Arrow */}
                                    {selectedPet.lostPet.breedimage && selectedPet.lostPet.breedimage.length > 1 && (
                                        <IconButton
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: 8,
                                                transform: 'translateY(-50%)',
                                                zIndex: 2,
                                                background: 'rgba(255,255,255,0.7)'
                                            }}
                                            onClick={() => setCarouselIndex((prev) =>
                                                prev === 0 ? selectedPet.lostPet.breedimage.length - 1 : prev - 1
                                            )}
                                        >
                                            <ChevronLeft />
                                        </IconButton>
                                    )}
                                    {/* Image */}
                                    <img
                                        src={selectedPet.lostPet.breedimage && selectedPet.lostPet.breedimage[carouselIndex]}
                                        alt=""
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            borderRadius: 8,
                                            display: "block",
                                        }}
                                    />
                                    {/* Right Arrow */}
                                    {selectedPet.lostPet.breedimage && selectedPet.lostPet.breedimage.length > 1 && (
                                        <IconButton
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                right: 8,
                                                transform: 'translateY(-50%)',
                                                zIndex: 2,
                                                background: 'rgba(255,255,255,0.7)'
                                            }}
                                            onClick={() => setCarouselIndex((prev) =>
                                                prev === selectedPet.lostPet.breedimage.length - 1 ? 0 : prev + 1
                                            )}
                                        >
                                            <ChevronRight />
                                        </IconButton>
                                    )}
                                </Box>
                                {/* Dots */}
                                {selectedPet.lostPet.breedimage && selectedPet.lostPet.breedimage.length > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        {selectedPet.lostPet.breedimage.map((_, idx) => (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    background: idx === carouselIndex ? '#FFB60C' : '#ddd',
                                                    mx: 0.5,
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => setCarouselIndex(idx)}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Box>
                            {/* Right: Details */}
                            <Box sx={{ flex: 2, minWidth: 0 }}>

                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                                    <Box sx={{ bgcolor: '#C6C6C6', borderRadius: 5, px: 2, py: 1, fontWeight: 700 }}>
                                        {t('SimilarityScore')}: {selectedPet.score}
                                    </Box>
                                    <Box sx={{ bgcolor: '#C6C6C6', borderRadius: 5, px: 2, py: 1, fontWeight: 700 }}>
                                        {t('LostDate')} : {selectedPet.lostPet.lostDate ? convertFromISO(selectedPet.lostPet.lostDate) : convertFromISO(selectedPet.lostPet.foundDate)}
                                    </Box>
                                    <Box sx={{ bgcolor: '#C6C6C6', borderRadius: 5, px: 2, py: 1, fontWeight: 700 }}>
                                        {t('LostLocation')}: {selectedPet.lostPet.lostLocation ? selectedPet.lostPet.lostLocation : selectedPet.lostPet.foundLocation}
                                    </Box>
                                </Box>
                                <Box component="table" sx={{ width: '100%', mb: 2 }}>
                                    <tbody>

                                        <tr>
                                            <td style={{ fontWeight: 700, paddingRight: 8, width: 150 }}>{t('Type')}</td>
                                            <td>{selectedPet.lostPet.animal || selectedPet.lostPet.type || ''}</td>
                                        </tr>
                                        <tr>
                                            <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                <Divider sx={{ my: 2 }} />
                                            </Box></td>
                                            <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                <Divider sx={{ my: 2 }} />
                                            </Box></td>
                                        </tr>

                                        <tr>
                                            <td style={{ fontWeight: 700, paddingRight: 8, width: 150 }}>{t('Features')}</td>
                                            <td>{selectedPet.lostPet.description}</td>
                                        </tr>
                                        <tr>
                                            <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                <Divider sx={{ my: 2 }} />
                                            </Box></td>
                                            <td><Box sx={{ width: "100%", mx: "auto", }}>
                                                <Divider sx={{ my: 2 }} />
                                            </Box></td>
                                        </tr>
                                        <tr>
                                            <td style={{ fontWeight: 700, paddingRight: 8, width: 150 }}>{t('Pet Status')}</td>
                                            <td>{selectedPet.lostPet.remarks}</td>
                                        </tr>
                                    </tbody>
                                </Box>
                                <Box sx={{ bgcolor: '#FFB60C', borderRadius: 2, px: 2, mt: 5, py: 1, fontWeight: 700, display: 'inline-block', width: '100%' }}>
                                    {t('Contact Information')}: &nbsp;
                                    <span style={{ fontWeight: 400 }}>{selectedPet.lostPet.owner} {selectedPet.lostPet.ownerContact1}</span>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
            <Footer />
        </Box>
    );
}
